import { useAtomValue } from "jotai";
import React, { Suspense, useEffect } from "react";
import { useQueryLoader } from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import { Box } from "@whoosh/design-system/src/components/primitive";

import {
  selectedFacilityId_atom,
  selectedPartySize_atom,
  selectedSeatingShift_atom,
} from "../atoms";
import {
  DietaryTags,
  OccasionTags,
} from "../hooks/__generated__/useUpdateDiningBookingMutation.graphql";
import {
  DiningAvailabilityList,
  DiningAvailabilityListQueryString,
} from "./DiningAvailabilityList";
import { DiningAvailabilityListQuery } from "./__generated__/DiningAvailabilityListQuery.graphql";

export type ExistingDiningBooking = {
  id: string;
  roomName: string;
  date: string;
  time: string;
  partySize: number;
  externalId: string;
  notesFromMember?: string | null;
  dietaryTags?: DietaryTags[] | null;
  occasionTags?: OccasionTags[] | null;
  ownerId?: string | null;
  otherPatronIds?: string[] | null;
};

type Props = {
  existingBooking?: ExistingDiningBooking;
  date?: string;
  firstDiningFacilityId?: string;
};
export const DiningAvailability = ({
  existingBooking,
  date,
  firstDiningFacilityId,
}: Props) => {
  const selectedPartySize = useAtomValue(selectedPartySize_atom);
  const selectedShift = useAtomValue(selectedSeatingShift_atom);
  const selectedFacilityId = useAtomValue(selectedFacilityId_atom);
  const currentFacilityId = selectedFacilityId || firstDiningFacilityId;

  const [listQueryRef, loadListQuery] =
    useQueryLoader<DiningAvailabilityListQuery>(
      DiningAvailabilityListQueryString
    );

  useEffect(() => {
    if (date && currentFacilityId && selectedPartySize)
      loadListQuery(
        {
          date,
          facilityId: currentFacilityId,
          partySize: selectedPartySize,
          startTime: selectedShift?.startTime || "00:00:00",
          endTime: selectedShift?.endTime || "23:59:59",
          reservationId: existingBooking?.externalId || undefined,
        },
        { fetchPolicy: "network-only" }
      );
  }, [
    date,
    existingBooking?.externalId,
    loadListQuery,
    currentFacilityId,
    selectedPartySize,
    selectedShift,
  ]);

  return (
    <Box pt={2} pb={4} flex={1} minHeight={200} bg="surface-secondary">
      <Suspense fallback={<Loader />}>
        {listQueryRef && (
          <DiningAvailabilityList
            queryRef={listQueryRef}
            existingBooking={existingBooking}
          />
        )}
      </Suspense>
    </Box>
  );
};
